// Wrappers para /api/lessons/*.

import { apiFetch } from './api';

export interface ModuleListItem {
  id: number;
  level_id: number;
  level_slug: string;
  level_label: string;
  code: string | null;
  title: string;
  year: number | null;
  order_index: number;
  is_published: number;
  legacy_post_id: number | null;
  lesson_count: number;
}

export interface LessonListItem {
  id: number;
  module_id: number | null;
  level_id: number;
  level_slug: string;
  level_label: string;
  title: string;
  slug: string;
  card_image_url: string | null;
  order_index: number;
  overview: string | null;
  warmup: string | null;
  is_published: number;
}

export async function listModules(levelId?: number): Promise<ModuleListItem[]> {
  const q = levelId ? `?level_id=${levelId}` : '';
  const r = await apiFetch<{ status: 'ok'; items: ModuleListItem[] }>(`/lessons/modules${q}`);
  return r.items;
}

export async function getModule(moduleId: number): Promise<{ module: ModuleListItem; lessons: LessonListItem[] }> {
  const r = await apiFetch<{ status: 'ok'; module: ModuleListItem; lessons: LessonListItem[] }>(`/lessons/modules/${moduleId}`);
  return { module: r.module, lessons: r.lessons };
}

export async function getLesson(lessonId: number): Promise<LessonListItem> {
  const r = await apiFetch<{ status: 'ok'; lesson: LessonListItem }>(`/lessons/${lessonId}`);
  return r.lesson;
}

// Admin
export interface CreateModuleInput {
  level_id: number;
  code?: string;
  title: string;
  year?: number;
  order_index?: number;
  is_published?: boolean;
}
export async function adminCreateModule(input: CreateModuleInput): Promise<{ id: number }> {
  return apiFetch<{ status: 'ok'; id: number }>('/lessons/admin/modules', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function adminDeleteModule(id: number): Promise<void> {
  await apiFetch(`/lessons/admin/modules/${id}`, { method: 'DELETE' });
}

export interface CreateLessonInput {
  module_id?: number;
  level_id: number;
  title: string;
  slug: string;
  card_image_url?: string;
  order_index?: number;
  overview?: string;
  warmup?: string;
  teacher_notes?: string;
  is_published?: boolean;
}
export async function adminCreateLesson(input: CreateLessonInput): Promise<{ id: number }> {
  return apiFetch<{ status: 'ok'; id: number }>('/lessons/admin/lessons', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
export async function adminDeleteLesson(id: number): Promise<void> {
  await apiFetch(`/lessons/admin/lessons/${id}`, { method: 'DELETE' });
}

// --- Contenido completo de una lección ---

export interface VocabItem {
  id?: number;
  position: number;
  word: string | null;
  phonetic: string | null;
  phonetic_image_url: string | null;
  learn_text: string | null;
  learn_image_url: string | null;
}

export type SpeakingMediaType = 'image' | 'video' | 'none';

export interface SpeakingBlock {
  id?: number;
  block_index: number;
  title: string | null;
  content: string | null;
  media_type: SpeakingMediaType;
  media_url: string | null;
}

export interface LessonActivity {
  id?: number;
  position: number;
  enunciado: string | null;
  quiz_id: number | null;
  quiz_title?: string | null;
}

export interface LessonContent {
  lesson: LessonListItem;
  vocab_items: VocabItem[];
  speaking_blocks: SpeakingBlock[];
  activities: LessonActivity[];
  visible_speaking_slots: number[];
  license_slug: string | null;
}

export async function getLessonContent(lessonId: number): Promise<LessonContent> {
  const r = await apiFetch<{ status: 'ok' } & LessonContent>(`/lessons/${lessonId}/content`);
  return {
    lesson: r.lesson,
    vocab_items: r.vocab_items,
    speaking_blocks: r.speaking_blocks,
    activities: r.activities,
    visible_speaking_slots: r.visible_speaking_slots,
    license_slug: r.license_slug,
  };
}

// Admin: replace subtablas
export async function adminReplaceVocab(lessonId: number, items: VocabItem[]): Promise<void> {
  await apiFetch(`/lessons/admin/lessons/${lessonId}/vocab`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
}

export async function adminReplaceSpeaking(lessonId: number, blocks: SpeakingBlock[]): Promise<void> {
  await apiFetch(`/lessons/admin/lessons/${lessonId}/speaking`, {
    method: 'PUT',
    body: JSON.stringify({ blocks }),
  });
}

export async function adminReplaceActivities(lessonId: number, activities: LessonActivity[]): Promise<void> {
  await apiFetch(`/lessons/admin/lessons/${lessonId}/activities`, {
    method: 'PUT',
    body: JSON.stringify({ activities }),
  });
}
